import type { ThroughputDataPoint } from "@situate/contracts/runtime";
import type { MultiLineChartPoint, MultiLineChartSeries } from "@/shared/charts";
import type { LineAreaChartPoint } from "@/shared/charts";
import type {
  ThroughputLineConfig,
  ThroughputLineTrendData,
  ThroughputTrendPoint,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";

export function getThroughputTrendErrorMessage(
  error: Error | string | null | undefined
): string | null {
  if (error == null) return null;
  return typeof error === "string" ? error : error.message;
}

/**
 * Returns the display name for a throughput line.
 * Special case: "Security Exit" is displayed as "IDL Ground Floor Entrance".
 */
export function getThroughputLineDisplayName(lineName: string): string {
  if (lineName === "Security Exit") {
    return "IDL Ground Floor Entrance";
  }
  return lineName;
}

/**
 * Determines if a throughput line requires data direction swap.
 * Special case: "Security Exit" has in/out reversed.
 */
function shouldSwapDirections(lineName: string): boolean {
  return lineName === "Security Exit";
}

export function mapThroughputDataPointsToTrend(
  data: readonly ThroughputDataPoint[],
  selectedLine: ThroughputLineConfig,
  getPeriod: (point: ThroughputDataPoint) => string
): ThroughputTrendPoint[] {
  const points = new Map<string, ThroughputTrendPoint>();
  const swapDirections = shouldSwapDirections(selectedLine.line_name);

  for (const point of data) {
    const isIn = point.direction_id === selectedLine.positive_direction.direction_id;
    const isOut = point.direction_id === selectedLine.negative_direction.direction_id;

    if (!isIn && !isOut) continue;

    const period = getPeriod(point);
    const trendPoint = points.get(period) ?? {
      period,
      timestamp: point.window_start,
      in: null,
      out: null,
    };

    // Swap in/out for "Security Exit"
    if (swapDirections) {
      if (isIn) trendPoint.out = point.count;
      if (isOut) trendPoint.in = point.count;
    } else {
      if (isIn) trendPoint.in = point.count;
      if (isOut) trendPoint.out = point.count;
    }
    points.set(period, trendPoint);
  }

  return [...points.values()];
}

export function computeThroughputTotals(points: ThroughputTrendPoint[]): {
  totalIn: number;
  totalOut: number;
  net: number;
} {
  let totalIn = 0;
  let totalOut = 0;
  for (const point of points) {
    if (point.in != null) totalIn += point.in;
    if (point.out != null) totalOut += point.out;
  }
  return { totalIn, totalOut, net: totalIn - totalOut };
}

export function mapThroughputTrendToMultiLineChartData(
  data: ThroughputTrendPoint[]
): MultiLineChartPoint[] {
  return data.map((point) => ({
    x: point.period,
    values: {
      in: point.in,
      out: point.out,
    },
  }));
}

/**
 * Retained for consumers of the previous single-series chart adapter.
 * New throughput charts should use mapThroughputTrendToMultiLineChartData.
 */
export function mapThroughputTrendToLineAreaChartData(
  data: ThroughputTrendPoint[]
): LineAreaChartPoint[] {
  return data.map((point) => ({
    x: point.period,
    timestamp: point.timestamp,
    value: point.in,
  }));
}

/**
 * Aggregates the selected lines into one In series and one Out series. Each direction retains a
 * consistent colour while the values represent the sum across all selected lines per period.
 */
export function mapLineTrendDataToChartSeries(
  lineTrendData: ThroughputLineTrendData[],
  directionColors: { in: string; out: string }
): { chartData: MultiLineChartPoint[]; series: MultiLineChartSeries[] } {
  const totalsByPeriod = new Map<
    string,
    { timestamp: string; in: number | null; out: number | null }
  >();

  for (const { points } of lineTrendData) {
    for (const point of points) {
      const totals = totalsByPeriod.get(point.period) ?? {
        timestamp: point.timestamp,
        in: null,
        out: null,
      };
      if (point.in != null) totals.in = (totals.in ?? 0) + point.in;
      if (point.out != null) totals.out = (totals.out ?? 0) + point.out;
      totalsByPeriod.set(point.period, totals);
    }
  }

  const chartData: MultiLineChartPoint[] = [...totalsByPeriod.entries()]
    .sort(([, a], [, b]) => a.timestamp.localeCompare(b.timestamp))
    .map(([period, totals]) => ({
      x: period,
      timestamp: totals.timestamp,
      values: { in: totals.in, out: totals.out },
    }));

  return {
    chartData,
    series: [
      { id: "in", label: "In", color: directionColors.in, lineStyle: "solid" },
      { id: "out", label: "Out", color: directionColors.out, lineStyle: "solid" },
    ],
  };
}
